import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass';

const VignetteShader = {
    uniforms: {
        tDiffuse: { value: null },
        offset:   { value: 1.05 },
        darkness: { value: 1.15 },
    },
    vertexShader: /* glsl */`
        varying vec2 vUv;
        void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */`
        uniform sampler2D tDiffuse;
        uniform float offset;
        uniform float darkness;
        varying vec2 vUv;
        void main() {
            vec4 c = texture2D(tDiffuse, vUv);
            vec2 uv = (vUv - 0.5) * offset;
            float v = clamp(1.0 - dot(uv, uv) * darkness * 0.9, 0.0, 1.0);
            gl_FragColor = vec4(c.rgb * mix(0.55, 1.0, v), c.a);
        }`,
};

// Quality tier detection — phones/tablets and low-core devices start on 'low'
export function detectTier() {
    try {
        const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
        const cores = navigator.hardwareConcurrency || 4;
        if (coarse || cores <= 4) return 'low';
    } catch (e) { /* ignore */ }
    return 'high';
}

export default class PostFX {
    constructor(renderer, scene, camera, tier) {
        this.renderer = renderer;
        this.scene = scene;
        this.camera = camera;
        this._build(tier);
    }

    _build(tier) {
        this.tier = tier;
        const r = this.renderer;
        r.setPixelRatio(Math.min(window.devicePixelRatio || 1, tier === 'high' ? 2 : 1.5));
        const size = r.getDrawingBufferSize(new THREE.Vector2());
        const rt = new THREE.WebGLRenderTarget(size.x, size.y, {
            type: THREE.HalfFloatType,
            samples: tier === 'high' ? 4 : 2,
        });
        this.composer = new EffectComposer(r, rt);
        this.composer.addPass(new RenderPass(this.scene, this.camera));
        if (tier === 'high') {
            const w = r.domElement.clientWidth || 1, h = r.domElement.clientHeight || 1;
            this.bloom = new UnrealBloomPass(new THREE.Vector2(w, h), 0.26, 0.5, 0.93);
            this.composer.addPass(this.bloom);
        } else {
            this.bloom = null;
        }
        this.composer.addPass(new ShaderPass(VignetteShader));
        this.composer.addPass(new OutputPass());
    }

    setTier(tier) {
        if (tier === this.tier) return;
        this.composer.dispose();
        this._build(tier);
        const c = this.renderer.domElement;
        this.setSize(c.clientWidth, c.clientHeight);
    }

    setSize(w, h) {
        this.composer.setPixelRatio(this.renderer.getPixelRatio());
        this.composer.setSize(w, h);
    }

    render(dt) {
        this.composer.render(dt);
    }

    dispose() {
        this.composer.dispose();
    }
}
